<template>
  <v-row
    class="fill-height"
    no-gutters
  >
    <BarChart
      :request-status="requestStatus"
      :series="series"
      :title="title"
      :item-id="itemId"
      :card-options="cardOptions"
      v-bind="$attrs"
      @reload="getData"
    />
  </v-row>
</template>

<script>
import EventBus from '@/utils/event-bus';
import { mapActions, mapGetters } from 'vuex';
import colors from '@/utils/colors';
import sentimentModel from '@/mixins/sentiment-model';
import BarChart from '@/components/cards/models/BarChart';
import { sortSeries } from '@/utils/sorting';

const {
  neutralColor,
  linkedinColor,
} = colors;

export default {
  name: 'BrandHealthByGroup',
  components: {
    BarChart,
  },
  mixins: [
    sentimentModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      pointsParser: 'series',
      filteredValues: [],
      cardOptions: {
        chart: {
          type: 'spline',
        },
        plotOptions: {
          column: {
            borderWidth: 0,
          },
        },
        xAxis: {
          categories: [],
          type: 'string',
        },
        yAxis: [{
          title: {
            text: this.$t('BrandHealth.BrandHealth'),
          },
          labels: {
            format: '{value}%',
          },
        },
        {
          title: {
            text: this.$t('BrandHealth.Mentions'),
          },
          opposite: true,
        },
        ],
        tooltip: {
          crosshairs: true,
        },
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', ['activeDashboardFilters', 'activeGlobalFilter']),
    series() {
      if (!this.rawSeries || !this.rawSeries.length) {
        return [];
      }

      let series = this.rawSeries;

      if (this.filteredValues.length) {
        series = series.filter(point => {
          return this.filteredValues.includes(point.name);
        }) || [];
      }

      const organizedSeries = series.map(serie => {
        return {
          name: serie.name || this.undefinedLabeL,
          data: serie.data.map((val) => val || 0),
          yAxis: serie.yAxis || 0,
          type: serie.type || 'spline',
          color: serie.color || neutralColor,
          zIndex: serie.zIndex,
          marker: {
            enabled: false,
          },
          tooltip: serie.tooltip,
        };
      });

      const sortedSeries = sortSeries(organizedSeries);
      return sortedSeries;
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getWarroomService',
    ]),
    orderDataArrayAsc(data) {
      return data.sort();
    },
    getSentimentData(points) {
      const brandHealth = [];
      const socialData = [];
      const categories = [];
      points.forEach(point => {
        socialData.push(point.total);
        categories.push(point.name);
        const percentPos = (point.positive / point.total) * 100;
        const percentNeu = (point.neutral / point.total) * 100;
        brandHealth.push(Number((percentNeu + percentPos).toFixed(1)));
      });
      return { brandHealth, totalPosts: socialData, categories };
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.getWarroomService({
        itemId: this.itemId,
        servicePath: 'monitoringMultibar',
        serviceParams: {
          type: this.type,
        },
      }).then(async (sentimentResponse) => {
        if (options?.silentRefresh) {
          EventBus.$emit('updated', { itemId: this.itemId });
        }
        this.requestStatus = 'success';
        let points = sentimentResponse.data.all;
        const activeFilter = this.activeGlobalFilter?.names?.value || this.$attrs.filter?.names?.value;
        if (this.type === 'names' && activeFilter) {
          points = points.filter(point => activeFilter.includes(point.name));
        }
        const { brandHealth, totalPosts, categories } = this.getSentimentData(points);
        this.cardOptions.xAxis.categories = categories;
        const series = [
          {
            yAxis: 1,
            name: this.$t('BrandHealth.Mentions'),
            data: totalPosts,
            color: linkedinColor,
            zIndex: 1,
            type: 'column',
          },
          {
            yAxis: 0,
            name: this.$t('BrandHealth.BrandHealth'),
            data: brandHealth,
            zIndex: 1,
            tooltip: {
              valueSuffix: '%',
            },
          },
        ];
        this.rawSeries = series;
      })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
  },
};
</script>

<style scoped lang="scss">
  ::v-deep {
    .line-chart-container,
    .line-chart {
      height: calc(100% - 40px) !important;
    }
  }
</style>
